import {
  HTTP_METHOD_GET,
  HTTP_METHOD_POST,
  NEXT_ACTION_TYPE_DONE,
  NEXT_ACTION_TYPE_REDIRECT,
} from '../constants';
import { createLogLocal } from '../utils/log-local';
import { useCommonOptionsStore } from '../store/useCommonOptionsStore';

import type { CommonOptionsStoreType } from '../store/useCommonOptionsStore';
import type { NextAction, NextActionDetails } from '../@types';
import type { PopupRedirectActionType } from 'components/payment-element/payment.types';

const log = createLogLocal('nextActionHandler.ts');

/**
 * Redirects the user to the given URL [url] in the current tab.
 */
export const redirectInCurrentTabWithGet = (url: string): void => {
  window.location.replace(url);
};

/**
 * Open the payment URL in a popup window.
 * @param {string} url URL to open in popup.
 * @param {function} openPopupCallback Callback from opening popup.
 *                                     Receives Window object from the popup.
 * @param {PopupRedirectActionType} opts Popup window options: height, width, etc.
 */
const openInPopupWithGet = (
  url: string,
  onPopupRendered: PopupRedirectActionType['onPopupRendered'],
  opts: PopupRedirectActionType['options'],
) => {
  opts.scrollbars ??= true;
  opts.resizable ??= true;
  opts.toolbar ??= false;
  opts.location ??= false;
  opts.menubar ??= false;

  const options = Object.keys(opts).map(key => {
    const val =
      typeof opts[key] === 'boolean' ? (opts[key] ? 'yes' : 'no') : opts[key];
    return `${key}=${val}`;
  });

  const optStr = `popup,${options.join(',')}`;
  const popup = window.open(url, 'paymentPopup', optStr);
  log(`openInPopupWithGet`, `popup:`, popup);

  onPopupRendered(popup);
};

/**
 * Handle redirect NextAction type. Either opens in the same tab, or in a popup.
 */
const handleRedirect = (
  details: NextActionDetails,
  redirectAction: CommonOptionsStoreType['redirectAction'],
  resolve: () => void,
  reject: () => void,
) => {
  log(`handleRedirect`, `details:`, details, `redirectAction:`, redirectAction);
  if (!('url' in details)) {
    resolve();
    return;
  }

  // Handle "do_nothing" action, which avoids opening an outside page (via
  // tab, redirect, or popup), in cases where this is expected (e.g. 3DSecure)
  if (redirectAction.type === 'do_nothing') {
    log(`handleRedirect`, `'do_nothing' redirectAction:`, redirectAction);
    resolve();
    return;
  }

  // Handle redirects with GET requests
  if (
    !('method' in details) ||
    String(details.method).toUpperCase() === HTTP_METHOD_GET
  ) {
    if (redirectAction.type === 'tab') {
      redirectAction.onTabRendered(window.open(details.url, 'paymentTab'));
    } else if (redirectAction.type === 'popup') {
      openInPopupWithGet(
        details.url,
        redirectAction.onPopupRendered,
        redirectAction.options,
      );
    } else {
      redirectInCurrentTabWithGet(details.url);
    }
    reject();
    return;
  }

  // Handle popups and tabs with POST or PUT requests
  if (redirectAction.type === 'tab') {
    redirectAction.onTabRendered(window.open(details.url, 'paymentTab'));
  } else if (redirectAction.type === 'popup') {
    openInPopupWithGet(
      details.url,
      redirectAction.onPopupRendered,
      redirectAction.options,
    );
    resolve();
    return;
  }

  // Handle standard redirect with POST or PUT requests
  if (redirectAction.type === 'navigate' || !(redirectAction as any)?.type) {
    if (
      String(details.method).toUpperCase() === HTTP_METHOD_POST &&
      details.postData
    ) {
      const redirectForm = document.createElement('form');
      redirectForm.setAttribute('action', details.url);
      redirectForm.setAttribute('method', 'post');
      redirectForm.setAttribute('hidden', 'true');

      Object.keys(details.postData).forEach((el: any) => {
        const input = document.createElement('input');
        input.setAttribute('type', 'hidden');
        input.setAttribute('name', el);
        input.setAttribute('value', details.postData![el]);
        redirectForm.appendChild(input);
      });

      document.body.appendChild(redirectForm);
      redirectForm.submit();
      reject();
      return;
    }

    resolve();
  }
};

/**
 * Handles the next action to be taken after a payment method form is submitted,
 * a redirect is requested, or a payment confirmation is required.
 *
 * @param {NextAction} nextAction Object representing the action.
 * @returns {Promise<void>} A promise that resolves when the action is completed
 */
const nextActionHandler = async (
  nextAction: NextAction,
  commonOptsStore?: CommonOptionsStoreType,
): Promise<void> => {
  log(`nextActionHandler`, `nextAction:`, nextAction);

  return new Promise<void>((resolve, reject) => {
    log(`nextActionHandler(promise)`, `commonOptsStore:`, commonOptsStore);

    if (nextAction?.type === NEXT_ACTION_TYPE_DONE && nextAction?.onSuccess) {
      nextAction.onSuccess().then(() => resolve());
      return;
    }

    if (nextAction?.type === NEXT_ACTION_TYPE_REDIRECT) {
      const nextActionDetails = nextAction.details || nextAction.redirect; // for backwards compatibility
      handleRedirect(
        nextActionDetails,
        commonOptsStore.redirectAction,
        resolve,
        reject,
      );
      return;
    }

    resolve();
  });
};

/**
 * Hook to return "next action" handler function.
 *
 * Note that a next action is generally an object returned from the server to
 * the frontend (e.g. from the /prepare endpoint).
 */
export const useNextActionHandler = () => {
  const commonOptions = useCommonOptionsStore();

  return (nextAction: NextAction) => {
    log('useNextActionHandler (λ)', 'nextAction:', nextAction);

    const response = nextActionHandler({ ...nextAction }, commonOptions);
    log('useNextActionHandler (λ)', 'response:', response);

    return response;
  };
};
